// MeiosPagamento.tsx
//
// Aba "Meios de Pagamento" de Lojas. Por filial, o dono da loja escolhe o que
// aceita NA ENTREGA (PIX, cartão na maquininha, dinheiro) e o que aceita DENTRO
// DO APP (PIX, cartão, se parcela e em quantas vezes).
//
// Onde isso vai: acd."ActSubsidiaryInfo", subsidiaryInfoType 20, uma linha por
// filial, e desce para aod."AotSubsidiaryInfo" pela fila acfg."FilaConfig".
//
// De onde as filiais vêm: do `decryptedInfoToken`, não de HTTP. `GET
// /subsidiaries` (plural) não existe no backend — a rota registrada é
// `/subsidiary*` — e o controller equivalente não faz `include`, então voltaria
// sem `companyName` e sem `ambients`. O padrão certo, já usado em Items.tsx, é
// ler a travessia do token, que também já traz `subsidiaryInfo`.

import React, { useEffect, useMemo, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/storesSection/MeiosPagamento";
import { subsidiaryService } from "../../../../services/subsidiary.service";
import { useDecodedToken } from "../../../../services/jwt-decode.service";
import { useCombinedStore } from "../../../../store";

/** O tipo é NOSSO, não do legado — ver subsidiaryInfo.enum.ts nos dois backends. */
const SUBSIDIARY_INFO_MEIOS_PAGAMENTO = 20;

const MEIOS_NA_ENTREGA = [
  { chave: "PIX", rotulo: "PIX" },
  { chave: "CARTAO_MAQUININHA", rotulo: "Cartão na maquininha" },
  { chave: "DINHEIRO", rotulo: "Dinheiro" },
] as const;

const MEIOS_NO_APP = [
  { chave: "PIX", rotulo: "PIX" },
  { chave: "CARTAO", rotulo: "Cartão" },
] as const;

const PARCELAS_MAXIMO = 12;

interface Configuracao {
  versao: number;
  naEntrega: { aceita: boolean; meios: string[] };
  noApp: {
    aceita: boolean;
    meios: string[];
    parcelas: { aceita: boolean; maximo: number };
  };
}

/** O que aparece quando a filial ainda não tem nada gravado. */
function configuracaoPadrao(): Configuracao {
  return {
    versao: 1,
    naEntrega: { aceita: true, meios: ["PIX", "CARTAO_MAQUININHA", "DINHEIRO"] },
    noApp: {
      aceita: true,
      meios: ["PIX"],
      parcelas: { aceita: false, maximo: 1 },
    },
  };
}

/**
 * Lê a configuração gravada da filial. JSON corrompido não pode branquear a
 * tela: cai no padrão e o usuário vê um aviso.
 */
function lerConfiguracao(filial: any): {
  configuracao: Configuracao;
  existe: boolean;
  corrompida: boolean;
} {
  const linha = (filial?.subsidiaryInfo ?? []).find(
    (info: any) =>
      Number(info?.subsidiaryInfoType) === SUBSIDIARY_INFO_MEIOS_PAGAMENTO
  );

  if (!linha) return { configuracao: configuracaoPadrao(), existe: false, corrompida: false };

  try {
    const bruto = JSON.parse(linha.subsidiaryInfoValue);
    const padrao = configuracaoPadrao();
    return {
      configuracao: {
        versao: typeof bruto?.versao === "number" ? bruto.versao : 1,
        naEntrega: {
          aceita: bruto?.naEntrega?.aceita === true,
          meios: Array.isArray(bruto?.naEntrega?.meios)
            ? bruto.naEntrega.meios
            : [],
        },
        noApp: {
          aceita: bruto?.noApp?.aceita === true,
          meios: Array.isArray(bruto?.noApp?.meios) ? bruto.noApp.meios : [],
          parcelas: {
            aceita: bruto?.noApp?.parcelas?.aceita === true,
            maximo:
              typeof bruto?.noApp?.parcelas?.maximo === "number"
                ? bruto.noApp.parcelas.maximo
                : 1,
          },
        },
      },
      existe: true,
      corrompida: false,
    };
  } catch {
    return { configuracao: configuracaoPadrao(), existe: true, corrompida: true };
  }
}

/**
 * Mesma regra do backend: "aceita" com a lista vazia é o mesmo que não aceitar.
 * O backend é a autoridade (o 400 dele manda); isto aqui só evita mandar o que
 * já se sabe que vai ser recusado.
 */
function aceitaDeVerdade(bloco: { aceita: boolean; meios: string[] }): boolean {
  return bloco.aceita && bloco.meios.length > 0;
}

function alternarMeio(meios: string[], chave: string): string[] {
  return meios.includes(chave)
    ? meios.filter((meio) => meio !== chave)
    : [...meios, chave];
}

interface Props {
  decryptedInfoToken: any;
}

const MeiosPagamento: React.FC<Props> = ({ decryptedInfoToken }) => {
  const { authUserZustand, setOrganizationZustand } = useCombinedStore(
    (state: any) => ({
      authUserZustand: state.authUserZustand,
      setOrganizationZustand: state.setOrganizationZustand,
    })
  );

  // `decryptedToken` NÃO vive na store: SettingsPage o deriva do token de auth
  // com este mesmo hook. Ler `state.decryptedToken` daria sempre undefined, e o
  // Salvar reclamaria de não saber quem está salvando.
  const decryptedToken: any = useDecodedToken(authUserZustand?.Token);

  // Travessia organization -> companies -> subsidiaries, no padrão de Items.tsx.
  // O hook do token pode devolver null no primeiro render, então tudo com `?.`.
  const filiais: any[] = useMemo(() => {
    const empresas = decryptedInfoToken?.organization?.companies ?? [];
    return empresas.flatMap((empresa: any) =>
      (empresa?.subsidiaries ?? []).map((filial: any) => ({
        ...filial,
        companyName: empresa?.companyName,
      }))
    );
  }, [decryptedInfoToken]);

  const [rascunhos, setRascunhos] = useState<Record<string, Configuracao>>({});
  const [avisos, setAvisos] = useState<
    Record<string, { tom: "ok" | "erro" | "atencao"; texto: string }>
  >({});
  const [aplicarEm, setAplicarEm] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState<Record<string, boolean>>({});
  const [fila, setFila] = useState<Record<string, any>>({});

  // Semeia os rascunhos com o que está gravado (e avisa se algum JSON estiver
  // corrompido — melhor um aviso que uma tela branca).
  useEffect(() => {
    if (filiais.length === 0) return;
    const iniciais: Record<string, Configuracao> = {};
    const avisosIniciais: Record<
      string,
      { tom: "ok" | "erro" | "atencao"; texto: string }
    > = {};
    for (const filial of filiais) {
      const { configuracao, corrompida } = lerConfiguracao(filial);
      iniciais[filial.subsidiaryUuid] = configuracao;
      if (corrompida) {
        avisosIniciais[filial.subsidiaryUuid] = {
          tom: "atencao",
          texto:
            "A configuração gravada não é um JSON válido. Abrindo no padrão do " +
            "sistema — salvar substitui o valor quebrado.",
        };
      }
    }
    setRascunhos(iniciais);
    setAvisos(avisosIniciais);
  }, [filiais]);

  // Situação da fila de cada filial: é a única maneira de o usuário descobrir
  // que a configuração dele virou conflito. Falha na leitura não derruba a aba.
  useEffect(() => {
    let ativo = true;
    for (const filial of filiais) {
      subsidiaryService
        .getFilaPorFilial(filial.subsidiaryUuid)
        .then((situacao) => {
          if (ativo) {
            setFila((atual) => ({ ...atual, [filial.subsidiaryUuid]: situacao }));
          }
        })
        .catch(() => {
          /* a aba funciona sem a fila; não vale quebrar a tela por isso */
        });
    }
    return () => {
      ativo = false;
    };
  }, [filiais]);

  const atualizar = (
    subsidiaryUuid: string,
    mudanca: (atual: Configuracao) => Configuracao
  ) => {
    setRascunhos((atuais) => ({
      ...atuais,
      [subsidiaryUuid]: mudanca(atuais[subsidiaryUuid] ?? configuracaoPadrao()),
    }));
  };

  const salvar = async (filial: any) => {
    const subsidiaryUuid = filial.subsidiaryUuid;
    const configuracao = rascunhos[subsidiaryUuid];
    if (!configuracao) return;

    const updatedBy = decryptedToken?.userInfo?.userUuid;
    if (!updatedBy) {
      setAvisos((atuais) => ({
        ...atuais,
        [subsidiaryUuid]: {
          tom: "erro",
          texto:
            "Não foi possível identificar quem está salvando. Entre novamente e tente de novo.",
        },
      }));
      return;
    }

    setSalvando((atuais) => ({ ...atuais, [subsidiaryUuid]: true }));
    try {
      const resposta = await subsidiaryService.updatePagamento(subsidiaryUuid, {
        valor: configuracao,
        aplicarEm:
          (aplicarEm[subsidiaryUuid] as "agora" | "proximoPeriodo") ?? "agora",
        updatedBy,
      });

      // O backend devolve o infoToken regenerado; sem gravá-lo, a tela continua
      // mostrando o valor antigo até o próximo login.
      if (resposta?.infoToken && setOrganizationZustand) {
        setOrganizationZustand({ InfoToken: resposta.infoToken });
      }

      // "Nada mudou" não é um salvamento: nada foi gravado e NADA vai descer
      // para o PDV. Pintar isso de verde, igual ao resto, ensina o dono a
      // confiar num aviso que não quer dizer o que ele acha que quer.
      setAvisos((atuais) => ({
        ...atuais,
        [subsidiaryUuid]: {
          tom: resposta?.semMudanca ? "atencao" : "ok",
          texto: resposta?.fila?.mensagem ?? "Alteração salva.",
        },
      }));

      const situacao = await subsidiaryService
        .getFilaPorFilial(subsidiaryUuid)
        .catch(() => null);
      if (situacao) {
        setFila((atual) => ({ ...atual, [subsidiaryUuid]: situacao }));
      }
    } catch (erro: any) {
      setAvisos((atuais) => ({
        ...atuais,
        [subsidiaryUuid]: {
          tom: "erro",
          texto:
            erro?.response?.data?.message ??
            "Não foi possível salvar os meios de pagamento.",
        },
      }));
    } finally {
      setSalvando((atuais) => ({ ...atuais, [subsidiaryUuid]: false }));
    }
  };

  if (!decryptedInfoToken) {
    return (
      <AlfredoStyles.FieldsContainer>
        <AlfredoStyles.LoadingContainer>
          <AlfredoStyles.LoadingSpinner />
          <div>Carregando lojas…</div>
        </AlfredoStyles.LoadingContainer>
      </AlfredoStyles.FieldsContainer>
    );
  }

  return (
    <AlfredoStyles.FieldsContainer>
      <AlfredoStyles.Header>
        <AlfredoStyles.HeaderContent>
          <AlfredoStyles.HeaderTitle>
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "calc(var(--u) * 36)" }}
            >
              payments
            </AlfredoStyles.MaterialIcons>
            Meios de Pagamento
          </AlfredoStyles.HeaderTitle>
          <AlfredoStyles.HeaderSubtitle>
            O que cada loja aceita na entrega e dentro do aplicativo do cliente
          </AlfredoStyles.HeaderSubtitle>
        </AlfredoStyles.HeaderContent>
      </AlfredoStyles.Header>

      {filiais.length === 0 ? (
        <AlfredoStyles.EmptyState>
          <AlfredoStyles.EmptyStateIcon>💳</AlfredoStyles.EmptyStateIcon>
          <AlfredoStyles.EmptyStateTitle>
            Nenhuma loja cadastrada
          </AlfredoStyles.EmptyStateTitle>
          <AlfredoStyles.EmptyStateDescription>
            Cadastre uma loja em "Lojas" para configurar os meios de pagamento.
          </AlfredoStyles.EmptyStateDescription>
        </AlfredoStyles.EmptyState>
      ) : (
        filiais.map((filial) => {
          const subsidiaryUuid = filial.subsidiaryUuid;
          const configuracao = rascunhos[subsidiaryUuid] ?? configuracaoPadrao();
          const aviso = avisos[subsidiaryUuid];
          const situacao = fila[subsidiaryUuid];

          const semNenhumMeio =
            !aceitaDeVerdade(configuracao.naEntrega) &&
            !aceitaDeVerdade(configuracao.noApp);

          return (
            <AlfredoStyles.FilialCard key={subsidiaryUuid}>
              <AlfredoStyles.FilialTitulo>
                <AlfredoStyles.MaterialIcons>store</AlfredoStyles.MaterialIcons>
                {filial.subsidiaryName}
              </AlfredoStyles.FilialTitulo>
              <AlfredoStyles.FilialSubtitulo>
                {filial.companyName}
              </AlfredoStyles.FilialSubtitulo>

              <AlfredoStyles.BlocosGrid>
                {/* ---------------- NA ENTREGA ---------------- */}
                <AlfredoStyles.Bloco>
                  <AlfredoStyles.BlocoTitulo>
                    <AlfredoStyles.MaterialIcons
                      style={{ fontSize: "var(--fs-base)" }}
                    >
                      delivery_dining
                    </AlfredoStyles.MaterialIcons>
                    Na entrega
                  </AlfredoStyles.BlocoTitulo>

                  <AlfredoStyles.Linha>
                    <AlfredoStyles.Caixa
                      type="checkbox"
                      checked={configuracao.naEntrega.aceita}
                      onChange={(e) =>
                        atualizar(subsidiaryUuid, (atual) => ({
                          ...atual,
                          naEntrega: {
                            ...atual.naEntrega,
                            aceita: e.target.checked,
                          },
                        }))
                      }
                    />
                    Aceita pagamento na entrega
                  </AlfredoStyles.Linha>

                  <AlfredoStyles.LinhaMeios
                    $desabilitada={!configuracao.naEntrega.aceita}
                  >
                    {MEIOS_NA_ENTREGA.map((meio) => (
                      <AlfredoStyles.Linha
                        key={meio.chave}
                        $desabilitada={!configuracao.naEntrega.aceita}
                      >
                        <AlfredoStyles.Caixa
                          type="checkbox"
                          disabled={!configuracao.naEntrega.aceita}
                          checked={configuracao.naEntrega.meios.includes(
                            meio.chave
                          )}
                          onChange={() =>
                            atualizar(subsidiaryUuid, (atual) => ({
                              ...atual,
                              naEntrega: {
                                ...atual.naEntrega,
                                meios: alternarMeio(
                                  atual.naEntrega.meios,
                                  meio.chave
                                ),
                              },
                            }))
                          }
                        />
                        {meio.rotulo}
                      </AlfredoStyles.Linha>
                    ))}
                  </AlfredoStyles.LinhaMeios>
                </AlfredoStyles.Bloco>

                {/* ---------------- NO APP ---------------- */}
                <AlfredoStyles.Bloco>
                  <AlfredoStyles.BlocoTitulo>
                    <AlfredoStyles.MaterialIcons
                      style={{ fontSize: "var(--fs-base)" }}
                    >
                      smartphone
                    </AlfredoStyles.MaterialIcons>
                    No aplicativo
                  </AlfredoStyles.BlocoTitulo>

                  <AlfredoStyles.Linha>
                    <AlfredoStyles.Caixa
                      type="checkbox"
                      checked={configuracao.noApp.aceita}
                      onChange={(e) =>
                        atualizar(subsidiaryUuid, (atual) => ({
                          ...atual,
                          noApp: { ...atual.noApp, aceita: e.target.checked },
                        }))
                      }
                    />
                    Aceita pagamento dentro do app
                  </AlfredoStyles.Linha>

                  <AlfredoStyles.LinhaMeios
                    $desabilitada={!configuracao.noApp.aceita}
                  >
                    {MEIOS_NO_APP.map((meio) => (
                      <AlfredoStyles.Linha
                        key={meio.chave}
                        $desabilitada={!configuracao.noApp.aceita}
                      >
                        <AlfredoStyles.Caixa
                          type="checkbox"
                          disabled={!configuracao.noApp.aceita}
                          checked={configuracao.noApp.meios.includes(meio.chave)}
                          onChange={() =>
                            atualizar(subsidiaryUuid, (atual) => ({
                              ...atual,
                              noApp: {
                                ...atual.noApp,
                                meios: alternarMeio(
                                  atual.noApp.meios,
                                  meio.chave
                                ),
                              },
                            }))
                          }
                        />
                        {meio.rotulo}
                      </AlfredoStyles.Linha>
                    ))}

                    <AlfredoStyles.Linha
                      $desabilitada={!configuracao.noApp.aceita}
                    >
                      <AlfredoStyles.Caixa
                        type="checkbox"
                        disabled={!configuracao.noApp.aceita}
                        checked={configuracao.noApp.parcelas.aceita}
                        onChange={(e) =>
                          atualizar(subsidiaryUuid, (atual) => ({
                            ...atual,
                            noApp: {
                              ...atual.noApp,
                              parcelas: {
                                aceita: e.target.checked,
                                // Desligou o parcelamento: o máximo volta para 1,
                                // igual à normalização do backend.
                                maximo: e.target.checked
                                  ? Math.max(atual.noApp.parcelas.maximo, 2)
                                  : 1,
                              },
                            },
                          }))
                        }
                      />
                      Aceita parcelar
                    </AlfredoStyles.Linha>

                    <AlfredoStyles.Linha
                      as="div"
                      $desabilitada={
                        !configuracao.noApp.aceita ||
                        !configuracao.noApp.parcelas.aceita
                      }
                    >
                      <AlfredoStyles.RotuloCampo>
                        Até
                      </AlfredoStyles.RotuloCampo>
                      <AlfredoStyles.Seletor
                        disabled={
                          !configuracao.noApp.aceita ||
                          !configuracao.noApp.parcelas.aceita
                        }
                        value={String(configuracao.noApp.parcelas.maximo)}
                        onChange={(e) =>
                          atualizar(subsidiaryUuid, (atual) => ({
                            ...atual,
                            noApp: {
                              ...atual.noApp,
                              parcelas: {
                                ...atual.noApp.parcelas,
                                maximo: Number(e.target.value),
                              },
                            },
                          }))
                        }
                      >
                        {Array.from(
                          { length: PARCELAS_MAXIMO },
                          (_, indice) => indice + 1
                        ).map((numero) => (
                          <option key={numero} value={String(numero)}>
                            {numero}x
                          </option>
                        ))}
                      </AlfredoStyles.Seletor>
                    </AlfredoStyles.Linha>
                  </AlfredoStyles.LinhaMeios>
                </AlfredoStyles.Bloco>
              </AlfredoStyles.BlocosGrid>

              <AlfredoStyles.AcaoRodape>
                <AlfredoStyles.RotuloCampo>Aplicar</AlfredoStyles.RotuloCampo>
                <AlfredoStyles.Seletor
                  value={aplicarEm[subsidiaryUuid] ?? "agora"}
                  onChange={(e) =>
                    setAplicarEm((atuais) => ({
                      ...atuais,
                      [subsidiaryUuid]: e.target.value,
                    }))
                  }
                >
                  <option value="agora">agora</option>
                  <option value="proximoPeriodo">no próximo caixa</option>
                </AlfredoStyles.Seletor>

                <AlfredoStyles.Button
                  disabled={semNenhumMeio || salvando[subsidiaryUuid]}
                  onClick={() => salvar(filial)}
                >
                  <AlfredoStyles.MaterialIcons
                    style={{ fontSize: "var(--fs-base)" }}
                  >
                    save
                  </AlfredoStyles.MaterialIcons>
                  {salvando[subsidiaryUuid] ? "Salvando…" : "Salvar"}
                </AlfredoStyles.Button>
              </AlfredoStyles.AcaoRodape>

              {semNenhumMeio && (
                <AlfredoStyles.Aviso $tom="erro">
                  A loja ficaria sem nenhum meio de pagamento. Aceite ao menos um
                  na entrega ou no app — com um meio marcado, o Salvar libera.
                </AlfredoStyles.Aviso>
              )}

              {aviso && (
                <AlfredoStyles.Aviso $tom={aviso.tom}>
                  {aviso.texto}
                </AlfredoStyles.Aviso>
              )}

              {situacao &&
                (situacao.pendentesAgora?.length > 0 ||
                  situacao.pendentesProximoPeriodo?.length > 0 ||
                  situacao.conflitos?.length > 0) && (
                  <AlfredoStyles.FilaCaixa>
                    <AlfredoStyles.FilaTitulo>
                      Situação da fila
                    </AlfredoStyles.FilaTitulo>
                    {situacao.pendentesAgora?.length > 0 && (
                      <AlfredoStyles.FilaItem>
                        <AlfredoStyles.MaterialIcons
                          style={{ fontSize: "var(--fs-base)" }}
                        >
                          sync
                        </AlfredoStyles.MaterialIcons>
                        {situacao.pendentesAgora.length} a caminho do PDV agora
                      </AlfredoStyles.FilaItem>
                    )}
                    {situacao.pendentesProximoPeriodo?.length > 0 && (
                      <AlfredoStyles.FilaItem>
                        <AlfredoStyles.MaterialIcons
                          style={{ fontSize: "var(--fs-base)" }}
                        >
                          schedule
                        </AlfredoStyles.MaterialIcons>
                        {situacao.pendentesProximoPeriodo.length} esperando a
                        abertura do próximo caixa
                      </AlfredoStyles.FilaItem>
                    )}
                    {situacao.conflitos?.map((conflito: any) => (
                      <AlfredoStyles.FilaItem key={conflito.eventoUuid}>
                        <AlfredoStyles.MaterialIcons
                          style={{ fontSize: "var(--fs-base)" }}
                        >
                          error_outline
                        </AlfredoStyles.MaterialIcons>
                        Recusado: {conflito.erro}
                      </AlfredoStyles.FilaItem>
                    ))}
                  </AlfredoStyles.FilaCaixa>
                )}
            </AlfredoStyles.FilialCard>
          );
        })
      )}
    </AlfredoStyles.FieldsContainer>
  );
};

export default MeiosPagamento;
